import React from 'react';

import {useDispatch, useSelector} from 'react-redux';
import {Link} from 'react-router';
import {Modal, ModalBody, ModalFooter} from 'reactstrap';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {useCurrentUserState} from 'app/current-user/api';
import {useAccountSettingsState} from 'app/settings/api';
import {ACCOUNT_OVERVIEW_URL} from 'app/urls';
import {gt} from 'app/utils/i18n';

import {
  selectIsHidingDataAccessInfoBox,
  toggleIsHidingDataAccessInfoBox,
} from '../slice';

type InfoModalProps = {
  isOpen: boolean;
  toggleModal: () => void;
};
export const PermitDataGatheringInfoModal = ({
  isOpen,
  toggleModal,
}: InfoModalProps) => {
  const {is_owner} = useCurrentUserState().data!;
  const {owner_email} = useAccountSettingsState().data!;

  const settingsOptionLabelText = gt('Allow share data with AI');
  const settingsOptionLabel = (
    <span>
      <br />
      {' "'}
      <i className="fa fa fa-robot m-1"></i>

      {settingsOptionLabelText}
      {'" '}
    </span>
  );

  return (
    <Modal isOpen={isOpen} toggle={toggleModal}>
      <ModalBody>
        <div className="p-3">
          <h3 className="font-weight-bold text-center">
            {gt('Allow SweetAI to access your company data*')}
          </h3>
          <p>
            {gt(
              'You are currently using SweetAI in a limited way. It does not know' +
                ' the specifics of your business and the generated content might not' +
                ' be of the best-possible quality.'
            )}
          </p>
          <p className="small">
            {gt('Without data sharing you may find that SweetAI:')}{' '}
            <ul className="list-group list-group-flush">
              <li className="list-group-item">
                {gt('Suggests creating documents that you already have')}
              </li>
              <li className="list-group-item">
                {gt(
                  'Writes procedures or policies that are not suited to your industry.'
                )}
              </li>
              <li className="list-group-item">
                {gt(
                  'Is prevented from editing existing documents on your account.'
                )}
              </li>
            </ul>
          </p>
          <p className=" text-center">
            {is_owner ? (
              <Link
                className="btn btn-link"
                to={`${ACCOUNT_OVERVIEW_URL}#allow-openai-data`}
              >
                {gt('Go to "Account Overview" and enable')}
                {settingsOptionLabel}
              </Link>
            ) : (
              <a
                href={`mailto:${owner_email}?subject=Enabling settings for "${settingsOptionLabelText}" in SweetProcess`}
              >
                {gt(
                  'Contact your SweetProcess Account Owner about enabling settings for:'
                )}
                {settingsOptionLabel}
              </a>
            )}
          </p>
          <p className="small font-italic">
            {'* '}
            {gt('The data shared with SweetAI is not used for training.')}
          </p>
        </div>
      </ModalBody>
      <ModalFooter>
        <button onClick={() => toggleModal()} className="btn btn-link btn-sm">
          <i className="fa fa-times" /> {gt('Cancel')}
        </button>
        <HideDataGatheringButton alternativeText={gt('Hide SweetAI prompts')} />
      </ModalFooter>
    </Modal>
  );
};

type HideButtonProps = {
  alternativeText?: string;
  className?: string;
};
export const HideDataGatheringButton = ({
  alternativeText,
  className,
}: HideButtonProps) => {
  const dispatch = useDispatch();
  const isHidingDataAccessInfoBox = useSelector(
    selectIsHidingDataAccessInfoBox
  );

  if (isHidingDataAccessInfoBox) {
    return null;
  }

  const hideDataGatheringInfoBox = (e) => {
    e.stopPropagation();
    dispatch(toggleIsHidingDataAccessInfoBox());
  };

  return (
    <LazyTooltip
      tooltip={gt(
        'I never want to be prompted to enable data sharing with SweetAI again'
      )}
    >
      <button
        onClick={hideDataGatheringInfoBox}
        className={`btn btn-link btn-sm ${className ? className : ''}`}
      >
        {alternativeText ? alternativeText : gt('Hide')}
      </button>
    </LazyTooltip>
  );
};
